import React, { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
} from "react-router-dom";
import RequireAuth from "./AuthRouter";
import Container from "./Container";
import { SocketProvider } from "../contexts/SocketContext";
import RouteLoader from "../components/common/RouteLoader";
import RouteAccessGuard from "../components/common/RouteAccessGuard";
import { RouteErrorBoundary } from "../components/common/ErrorBoundary";

// Os endereços antigos de modelos (e-mail e WhatsApp) foram unificados em
// /mensagens. O redirect mantém a querystring (ex.: ?tenantId=) e escolhe o
// filtro do canal correspondente.
const LegacyTemplatesRedirect = ({ canal }) => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set("canal", canal);
  return <Navigate to={`/mensagens?${params.toString()}`} replace />;
};

// Páginas carregadas sob demanda (code-splitting por rota).
// Cada uma vira um chunk separado, então o bundle inicial fica pequeno.
const LoginPage = lazy(() => import("../pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("../pages/ForgotPasswordPage"));
const NotFoundPage = lazy(() => import("../pages/NotFoundPage"));
const Dashboard = lazy(() => import("../components/Dashboard"));
const BookingPublicPage = lazy(() => import("../pages/BookingPublicPage"));
const BookingManagePage = lazy(() => import("../pages/BookingManagePage"));
const BookingSettingsPage = lazy(() => import("../pages/BookingSettingsPage"));
const MyProfilePage = lazy(() => import("../pages/MyProfilePage"));
const SupportTable = lazy(() => import("../components/tables/SupportTable"));
const DocsTable = lazy(() => import("../components/tables/DocsTable"));
const UsersTable = lazy(() => import("../components/tables/UsersTable"));
const AdminCustomersTable = lazy(() =>
  import("../components/tables/AdminCustomersTable")
);
const AdminCustomerForm = lazy(() =>
  import("../components/forms/AdminCustomerForm")
);
const InvoicesTable = lazy(() => import("../components/tables/InvoicesTable"));
const AdminPaymentsTable = lazy(() =>
  import("../components/tables/AdminPaymentsTable")
);
const AdminUsersTable = lazy(() =>
  import("../components/tables/AdminUsersTable")
);
const FormPage = lazy(() => import("../pages/FormPage"));
const CardFormPage = lazy(() => import("../pages/CardFormPage"));
const PublicFormsPage = lazy(() => import("../pages/PublicFormsPage"));
const CRMPage = lazy(() => import("../components/tables/CRMPage"));
const FunnelManager = lazy(() => import("../components/tables/FunnelManager"));
const TasksTodayPage = lazy(() => import("../pages/TasksTodayPage"));
// Módulos adicionados depois: RBAC, cadências, lead scoring e área do dev.
const CadencesPage = lazy(() => import("../pages/CadencesPage"));
const CadenceEditorPage = lazy(() => import("../pages/CadenceEditorPage"));
const LeadScoringPage = lazy(() => import("../pages/LeadScoringPage"));
const DeveloperPage = lazy(() => import("../pages/DeveloperPage"));
const CalendarPage = lazy(() => import("../pages/CalendarPage"));
const TeamManagerPage = lazy(() => import("../pages/TeamManagerPage"));
const SignupPage = lazy(() => import("../pages/SignUpPage"));
const StageFormBuilder = lazy(() =>
  import("../components/crm/StageFormBuilder")
);
const FunnelPublicFormBuilder = lazy(() =>
  import("../components/crm/FunnelPublicFormBuilder")
);
const CardPublicFormBuilder = lazy(() =>
  import("../components/crm/CardPublicFormBuilder")
);
const ActivityList = lazy(() => import("../pages/ActivityList"));
const FunnelsGallery = lazy(() => import("../components/tables/FunnelGallery"));
const FirstAccessOnboarding = lazy(() =>
  import("../pages/FirstAccessOnboarding")
);
const ChatApp = lazy(() => import("../pages/ChatApp"));
const GlobalMeetingInviteOverlay = lazy(() =>
  import("../components/common/GlobalMeetingInviteOverlay")
);
const SalesInsightsPage = lazy(() => import("../pages/SalesInsightsPage"));
const CustomFieldsPage = lazy(() => import("../pages/CustomFieldsPage"));
const ReportsOverviewPage = lazy(() => import("../pages/ReportsOverviewPage"));
const MetricBuilderPage = lazy(() => import("../pages/MetricBuilderPage"));
const VirtualOfficePerformancePage = lazy(() =>
  import("../pages/VirtualOfficePerformancePage")
);
const VoipCallsPage = lazy(() => import("../pages/VoipCallsPage"));
const LeadGeneratorPage = lazy(() => import("../pages/LeadGeneratorPage"));
const AutomationPage = lazy(() => import("../pages/AutomationPage"));
const BudgetGeneratorPage = lazy(() => import("../pages/BudgetGeneratorPage"));
const BudgetListPage = lazy(() => import("../pages/BudgetListPage"));
const ProductCatalogPage = lazy(() => import("../pages/ProductCatalogPage"));
const LeadSourcesPage = lazy(() => import("../pages/LeadSourcesPage"));
const LeadTagsPage = lazy(() => import("../pages/LeadTagsPage"));
const LeadCustomFieldsPage = lazy(() =>
  import("../pages/LeadCustomFieldsPage")
);
const GoogleCalendarCallbackPage = lazy(() =>
  import("../pages/GoogleCalendarCallbackPage")
);
const MicrosoftCalendarCallbackPage = lazy(() =>
  import("../pages/MicrosoftCalendarCallbackPage")
);
const OutlookEmailCallbackPage = lazy(() =>
  import("../pages/OutlookEmailCallbackPage")
);
const GmailEmailCallbackPage = lazy(() =>
  import("../pages/GmailEmailCallbackPage")
);
const MessageTemplatesPage = lazy(() => import("../pages/MessageTemplatesPage"));
const PrivacyPolicyPage = lazy(() => import("../pages/PrivacyPolicyPage"));
const ManageSubscriptionPage = lazy(() =>
  import("../pages/ManageSubscriptionPage")
);
const SubscriptionPaymentPage = lazy(() =>
  import("../pages/SubscriptionPaymentPage")
);
const ImpersonatePage = lazy(() => import("../pages/ImpersonatePage"));
const ImportLeadsPage = lazy(() => import("../pages/ImportLeadsPage"));
const ImportProductsPage = lazy(() => import("../pages/ImportProductsPage"));
const ProductUnitsPage = lazy(() => import("../pages/ProductUnitsPage"));
const WhatsAppChatPage = lazy(() =>
  import("../pages/WhatsAppChatPageModernV2")
);
const WhatsAppFlowsPage = lazy(() => import("../pages/WhatsAppFlowsPage"));
const WhatsAppFlowEditorPage = lazy(() =>
  import("../pages/WhatsAppFlowEditorPage")
);
const WhatsAppAiAgentConfigPage = lazy(() =>
  import("../pages/WhatsAppAiAgentConfigPage")
);
const WhatsAppWebPage = lazy(() => import("../pages/WhatsAppWebPage"));
const GoalsPage = lazy(() => import("../pages/GoalsPage"));
const NewGoalPage = lazy(() => import("../pages/NewGoalPage"));
const IntegrationsPage = lazy(() => import("../pages/IntegrationsPage"));
const EmpresaPage = lazy(() => import("../pages/EmpresaPage"));
const MetaAdsIntegrationPage = lazy(() =>
  import("../pages/MetaAdsIntegrationPage")
);

const AppRouter = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteLoader />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/impersonate" element={<ImpersonatePage />} />
          <Route path="/form/:slug" element={<FormPage />} />
          <Route path="/card-form/:cardId" element={<CardFormPage />} />
          <Route path="/agendar/:slug" element={<BookingPublicPage />} />
          <Route path="/agendamento/:token" element={<BookingManagePage />} />
          <Route path="/onboarding" element={<FirstAccessOnboarding />} />
          <Route path="/politica-privacidade" element={<PrivacyPolicyPage />} />

          <Route
            path="/"
            element={
              <RequireAuth>
                <SocketProvider>
                  {/* O dock do chat é montado pelo Sidebar (com as props de abrir/
                      não lidas). Uma 2ª instância aqui repetia, a cada carga, a
                      busca de usuários/presença e os listeners de socket. */}
                  <GlobalMeetingInviteOverlay />
                  <Container>
                    {/* Uma barreira por rota: erro de render numa tela não
                        derruba o app inteiro, e navegar para outra já a
                        restaura (a chave é o pathname). */}
                    <RouteErrorBoundary>
                      {/* Suspense interno: enquanto o chunk da tela carrega, só a
                          área da página mostra o loader — o menu lateral segue
                          visível (e o contorno do item ativo desliza). */}
                      <Suspense fallback={<RouteLoader inline />}>
                        {/* Tela que o papel não libera mostra um aviso (chave
                            por rota em utils/routeAccess.js). */}
                        <RouteAccessGuard>
                          <Outlet />
                        </RouteAccessGuard>
                      </Suspense>
                    </RouteErrorBoundary>
                  </Container>
                </SocketProvider>
              </RequireAuth>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="users" element={<UsersTable />} />
            <Route path="crm" element={<CRMPage />} />
            <Route path="crm/tasks" element={<TasksTodayPage />} />
            <Route path="calendario" element={<CalendarPage />} />
            <Route path="agendamentos" element={<BookingSettingsPage />} />
            <Route path="crm/automations" element={<AutomationPage />} />
            <Route path="budget" element={<BudgetGeneratorPage />} />
            <Route path="budgets" element={<BudgetListPage />} />
            <Route path="products" element={<ProductCatalogPage />} />
            <Route path="leads/generator" element={<LeadGeneratorPage />} />
            <Route path="funnels" element={<FunnelManager />} />
            <Route path="crm/fields" element={<CustomFieldsPage />} />
            <Route
              path="crm/public-forms/:funnelId"
              element={<PublicFormsPage />}
            />
            <Route path="reports" element={<ReportsOverviewPage />} />
            <Route path="reports/metrics/new" element={<MetricBuilderPage />} />
            <Route
              path="desempenho/escritorio"
              element={<VirtualOfficePerformancePage />}
            />
            <Route path="desempenho/ligacoes" element={<VoipCallsPage />} />
            <Route path="board" element={<FunnelsGallery />} />
            <Route path="teams" element={<TeamManagerPage />} />
            {/* Papéis agora é uma aba de Usuários e permissões; mantém links antigos. */}
            <Route path="papeis" element={<Navigate to="/users" replace />} />
            <Route path="desenvolvedor" element={<DeveloperPage />} />
            <Route path="cadencias" element={<CadencesPage />} />
            <Route path="cadencias/:cadenceId" element={<CadenceEditorPage />} />
            <Route path="contacts/pontuacao" element={<LeadScoringPage />} />
            <Route path="metas" element={<GoalsPage />} />
            <Route path="metas/nova" element={<NewGoalPage />} />
            <Route path="invoices" element={<InvoicesTable />} />
            <Route path="activities" element={<ActivityList />} />
            <Route
              path="contacts/custom-fields"
              element={<LeadCustomFieldsPage />}
            />
            <Route path="contacts/sources" element={<LeadSourcesPage />} />
            <Route path="contacts/tags" element={<LeadTagsPage />} />
            <Route
              path="/crm/stages/:stageId/form"
              element={<StageFormBuilder />}
            />
            <Route
              path="/crm/funnels/:funnelId/public-form"
              element={<FunnelPublicFormBuilder />}
            />
            <Route
              path="/crm/funnels/:funnelId/card-public-forms"
              element={<CardPublicFormBuilder />}
            />
            <Route path="contacts" element={<AdminCustomersTable />} />
            <Route path="leads/import" element={<ImportLeadsPage />} />
            <Route path="products/import" element={<ImportProductsPage />} />
            <Route path="products/units" element={<ProductUnitsPage />} />
            <Route path="admin/users" element={<AdminUsersTable />} />
            <Route path="admin/new-customer" element={<AdminCustomerForm />} />
            <Route path="admin/payments" element={<AdminPaymentsTable />} />
            <Route path="docs" element={<DocsTable />} />
            <Route path="chat" element={<ChatApp />} />
            <Route path="whatsapp" element={<WhatsAppChatPage />} />
            <Route path="whatsapp/web" element={<WhatsAppWebPage />} />
            <Route path="whatsapp/flows" element={<WhatsAppFlowsPage />} />
            <Route
              path="whatsapp/flows/:flowId"
              element={<WhatsAppFlowEditorPage />}
            />
            {/* Antigo /whatsapp/templates: virou a aba de WhatsApp de /mensagens
                (preserva ?tenantId=). */}
            <Route
              path="whatsapp/templates"
              element={<LegacyTemplatesRedirect canal="whatsapp" />}
            />
            <Route
              path="whatsapp/ai-agent"
              element={<WhatsAppAiAgentConfigPage />}
            />
            <Route path="profile" element={<MyProfilePage />} />
            <Route path="empresa" element={<EmpresaPage />} />
            <Route path="integracoes" element={<IntegrationsPage />} />
            <Route path="mensagens" element={<MessageTemplatesPage />} />
            <Route
              path="modelos-de-email"
              element={<LegacyTemplatesRedirect canal="email" />}
            />
            <Route
              path="integracoes/meta-ads"
              element={<MetaAdsIntegrationPage />}
            />
            <Route path="assinatura" element={<ManageSubscriptionPage />} />
            <Route
              path="assinatura/pagamento"
              element={<SubscriptionPaymentPage />}
            />

            <Route path="support" element={<SupportTable />} />
            <Route path="insights" element={<SalesInsightsPage />} />
            <Route
              path="/integrations/google-calendar/callback"
              element={<GoogleCalendarCallbackPage />}
            />
            <Route
              path="/integrations/microsoft-calendar/callback"
              element={<MicrosoftCalendarCallbackPage />}
            />
            <Route
              path="/integrations/outlook-email/callback"
              element={<OutlookEmailCallbackPage />}
            />
            <Route
              path="/integrations/gmail-email/callback"
              element={<GmailEmailCallbackPage />}
            />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default AppRouter;
